import {
  BlogCommentDocument,
  blogCommentDocumentSchema,
  BlogDocument,
} from '@util/types/firestore/blog';
import { ApiResponse } from '@util/firestore/products';
import { DeleteCommentArgs, GetBlogCommentsArgs } from './blog.types';
import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where
} from 'firebase/firestore';
import { db } from '@util/firebase';
import { cleanHtmlString } from '@util/htmlHelpers';
import { fetchFromWordpress } from './blog.utils';
import axios from 'axios';

const blogCommentsRef = collection(
  db, 'blog_comments'
) as CollectionReference<BlogCommentDocument>;

export type CursorPagination<T> = {
  prev: T | null;
  next: T | null;
  currentCursor: T | null;
  direction: 'next' | 'prev' | null;
  totalResults: number;
};

type GetBlogsResponse = {
  results: Partial<BlogDocument>[];
  pageInfo: CursorPagination<string>;
};

export async function getBlogs(
  category: string | null = null,
  cursor: string | null = null,
  direction: string | null = null
): Promise<GetBlogsResponse> {
  try {
    const variables: Record<string, any> = {
      first: 10,
      last: null
    };

    if (cursor && direction === "next") {
      variables.after = cursor
      variables.first = 10
    }

    if (cursor && direction === "prev") {
      variables.before = cursor
      variables.last = 10
      variables.first = null
    }

    const queryId = category ? 'byCategory' : 'posts';
    if (category) {
      variables.categorySlug = category;
    }

    const wpData = await fetchFromWordpress<any[]>('graphql', {
      queryId,
      variables,
    });

    if (!wpData?.results?.length) {
      return {
        results: [],
        pageInfo: {
          totalResults: 0,
          currentCursor: cursor,
          direction: null,
          next: null,
          prev: null,
        },
      };
    }

    const posts = await Promise.all(
      wpData.results.map(async (page: any): Promise<Partial<BlogDocument>> => {
        const post = page.node;
        const media = post.featuredImage?.node;

        return {
          id: post.firebaseid || post.id,
          title: cleanHtmlString(post.title),
          description: cleanHtmlString(post.excerpt),
          slug: post.slug,
          created: post.date,
          thumbnail: media?.guid || media?.sourceUrl || "",
          categories: post.categories?.nodes?.map((cat: any) => {
            return {
              name: cat.name,
              slug: cat.slug
            }
          }) || [],
          tags: []
        };
      })
    );

    console.log("wpData.pageInfo", wpData.pageInfo)

    return {
      results: posts,
      pageInfo: {
        totalResults: posts.length,
        currentCursor: wpData.pageInfo?.endCursor || null,
        direction: null,
        next: wpData.pageInfo?.hasNextPage ? wpData.pageInfo.endCursor : null,
        prev: wpData.pageInfo?.hasPreviousPage ? wpData.pageInfo.startCursor : null,
      },
    };
  } catch (err) {
    console.error('❌ Error fetching paginated blogs:', err);
    return {
      results: [],
      pageInfo: {
        totalResults: 0,
        currentCursor: null,
        direction: null,
        next: null,
        prev: null,
      },
    };
  }
}

export async function getNBlogs(n: number): Promise<ApiResponse<Partial<BlogDocument>[]>> {
  try {
    const wpData = await fetchFromWordpress<any[]>('graphql', {
      queryId: 'posts',
      variables: {"first": n, "after": null}
    });

    if (!wpData?.results?.length) return { results: [] };

    const posts = await Promise.all(
      wpData.results.map(async (page: any): Promise<Partial<BlogDocument>> => {
        const post = page.node;
        // WPGraphQL returns featuredImage: null when a post has no featured
        // image, so every hop must be optional — an unguarded deref here
        // rejects the whole Promise.all and hides the entire homepage
        // section. SafeImage renders /not_found.svg for an empty thumbnail.
        const media = post.featuredImage?.node;

        return {
          title: cleanHtmlString(post.title),
          description: cleanHtmlString(post.excerpt),
          slug: post.slug,
          created: post.date,
          thumbnail: media?.guid || media?.sourceUrl || '',
          categories: post.categories?.nodes?.map((cat: any) => {
            return {
              name: cat.name,
              slug: cat.slug
            }
          }) || [],
          tags: []
        };
      })
    );

    return { results: posts };
  } catch (err) {
    // Matches getBlogs: a WordPress failure degrades to an empty section
    // rather than an unhandled rejection that silently blanks the homepage.
    console.error('❌ Error fetching recent blogs:', err);
    return { results: [] };
  }
}

/** Guards the missing-credentials log so it fires once per process, not per render. */
let warnedMissingWordpressCredentials = false;

/**
 * Fetch a post's WordPress `post_modified` timestamp.
 *
 * WHY THIS EXISTS AS A SEPARATE CALL: the site reads posts through a *persisted*
 * GraphQL query (`queryId: 'post'`) whose text lives on the WordPress server, not
 * in this repo. That saved query does not select `modified` — measured, the key is
 * absent from the response, not null. An ad-hoc query on the same endpoint does
 * return it. So until the saved query is amended server-side, one extra minimal
 * query is the only way to obtain a true modified date, and reading
 * `post.modified` off the persisted response would silently yield undefined
 * forever.
 *
 * SAFETY CONTRACT — this function must never be able to break a blog page:
 *  - it never throws; every failure path returns undefined
 *  - it carries its own short timeout so a slow WordPress cannot stall an SSR render
 *  - undefined simply means "unknown", and callers then omit dateModified,
 *    which is exactly the behaviour that shipped before this change
 */
async function fetchPostModified(slug: string): Promise<string | undefined> {
  // ⛔ These are deliberately NOT prefixed with NEXT_PUBLIC_, and must never be.
  //
  // An earlier revision also read `NEXT_PUBLIC_WORDPRESS_*` as a merge-order
  // fallback. That was removed as a required fix: in Next.js the prefix is an
  // instruction to inline the literal value into the browser bundle, and this
  // module IS client-reachable (`src/utils/blog/index.ts` re-exports
  // blog.service, and the client component BlogCommentSection imports the
  // barrel). Reading a prefixed name here re-opened the exact credential leak
  // that the concurrent WordPress-credentials fix exists to close — invisible in
  // this file's diff, visible only by reading both branches together.
  //
  // Unprefixed reads are safe even from a client-reachable module: without the
  // prefix Next.js performs no substitution, so in a browser these resolve
  // against an empty shim and the guard below returns undefined. Measured on
  // real build output by the credentials session, with a positive control.
  //
  // Consequence, accepted deliberately: this needs WORDPRESS_URL / _USER / _PASS
  // present in Vercel. Until they are, the guard below short-circuits and the
  // page falls back to today's behaviour — inert, never broken.
  const wpUrl = process.env.WORDPRESS_URL;
  const wpUser = process.env.WORDPRESS_USER;
  const wpPass = process.env.WORDPRESS_PASS;

  if (!wpUrl || !wpUser || !wpPass) {
    // Log ONCE per process, not per render. This is the likeliest inert state by
    // far: the feature needs WORDPRESS_URL/_USER/_PASS in Vercel, and if they are
    // absent it omits dateModified on every post. That must not be silent.
    // Once-per-process because a per-render log on a template this hot would be
    // noise that trains people to ignore it.
    if (!warnedMissingWordpressCredentials) {
      warnedMissingWordpressCredentials = true;
      console.error(
        '[blog:modified] WORDPRESS_URL/WORDPRESS_USER/WORDPRESS_PASS are not all set; dateModified will be omitted on every post until they are'
      );
    }
    return undefined;
  }

  try {
    // `errors` is part of the shape because GraphQL reports rejections with
    // HTTP 200 and an errors array rather than a transport-level failure.
    const response = await axios.get<{
      data?: { post?: { modified?: string } };
      errors?: { message?: string }[];
    }>(
      `${wpUrl}/graphql`,
      {
        auth: { username: wpUser, password: wpPass },
        params: {
          query: 'query($id:ID!){post(id:$id,idType:SLUG){modified}}',
          variables: JSON.stringify({ id: slug }),
        },
        // Cost of this call, measured rather than reasoned — the first estimate
        // here was wrong and is worth recording so nobody repeats it.
        //
        // Timing the two queries in isolation said the sidecar (0.94-1.15s) is
        // faster than the persisted query it accompanies (1.58-1.74s), and the
        // conclusion drawn was "concurrent, therefore free". That does not
        // follow: the isolated timing is a floor. It misses contention at the
        // WordPress origin and the extra connection setup from the serving
        // region.
        //
        // Difference-in-differences against /blog (same SSR, same origin, no
        // sidecar) as a control page cancels the deployment confound. Two
        // independent runs: medians +68ms and +82ms, means +111ms and +4ms. The
        // medians agree; the means are unstable at n<=8, so treat this as
        // ~70-80ms on a cold render against a ~2s baseline, not a precise figure.
        //
        // Users mostly do not pay it: these pages carry
        // stale-while-revalidate=900, so between 300s and 1200s the CDN serves
        // stale instantly and revalidates out of band. The cost lands on
        // genuinely cold URLs — long-tail posts and crawlers.
        //
        // The ceiling below is ~1.7x the slowest observed run of this query,
        // bounding the damage if WordPress ever hangs on this call alone.
        timeout: 2000,
      }
    );

    // A GraphQL rejection arrives as HTTP 200 with an `errors` array, so it never
    // reaches the catch below. Without this branch a persisted-only endpoint, or a
    // WAF that strips the query string, would make the whole feature inert on every
    // post with no signal anywhere — and because BlogPostingSchema falls back to
    // `dateModified || datePublished`, that inert state is byte-identical to the bug
    // being fixed. Silence is the one failure mode nobody would ever notice.
    const errors = response.data?.errors;
    if (Array.isArray(errors) && errors.length) {
      console.error(
        '[blog:modified] WordPress returned GraphQL errors; dateModified will be omitted:',
        errors.map((e) => e?.message).join('; ')
      );
      return undefined;
    }

    const modified = response.data?.data?.post?.modified;
    if (typeof modified === 'string' && modified.trim()) return modified;

    // warn, not error. A slug that does not exist reaches this branch too
    // (WPGraphQL returns post: null with no errors array), and the caller then
    // redirects to /blog — the correct 404 path, not a failure. Any crawler
    // walking bogus /blog/<garbage> URLs would otherwise write an error-level
    // line per request into the very log you would scan to answer "did the
    // sidecar work?", burying the real signal under noise. The two genuine
    // failure branches stay at error level so a filter on `error` shows only
    // states that need attention.
    console.warn(
      `[blog:modified] no usable modified field for slug "${slug}" (got ${typeof modified}); dateModified will be omitted. Expected for a slug that does not exist.`
    );
    return undefined;
  } catch (err) {
    // The page still renders — a missing freshness date is a cosmetic metadata gap,
    // never a reason to fail the article. But it is logged, because an undiagnosable
    // silent no-op is how a "shipped" fix stays dead for months.
    console.error(
      `[blog:modified] fetch failed for slug "${slug}"; dateModified will be omitted:`,
      err instanceof Error ? err.message : err
    );
    return undefined;
  }
}

export async function getBlogBySlug(slug: string): Promise<BlogDocument | null> {
  const formattedSlug = decodeURIComponent(slug).toLowerCase();

  // Issued concurrently with the main post query so the extra round trip adds no
  // wall-clock latency to the render in the normal case.
  const [wpData, modified] = await Promise.all([
    fetchFromWordpress<any[]>('graphql', {
      queryId: 'post',
      variables: { "id": formattedSlug, "idType": "SLUG" }
    }),
    fetchPostModified(formattedSlug),
  ]);

  if (!wpData?.results) return null;

  const post = wpData.results;

  const blogDoc: BlogDocument = {
    id: post.firebaseid || post.id || "",
    title: cleanHtmlString(post.title),
    description: cleanHtmlString(post.excerpt),
    html: post.content,
    slug: post.slug,
    created: post.date,
    ...(modified && { modified }),
    is_featured: false,
    uid: '',
    author: post.author?.node?.name || 'GearFocus',
    thumbnail: post.featuredImage?.node?.guid || '',
    full_image: post.featuredImage?.node?.guid || '',
    is_published: true,
    categories: post.categories?.nodes?.map((cat: any) => ({
      name: cat.name,
      slug: cat.slug,
    })) || [],
    tags: [],
    meta_tags: {
      title: post.seo?.title || cleanHtmlString(post.title),
      keywords: '',
      description: post.seo?.description || cleanHtmlString(post.excerpt),
    },
  };

  return blogDoc
}

export async function getAllCategories(): Promise<{name: string, slug: string}[]> {
  const wpData = await fetchFromWordpress<any[]>('graphql', {
    queryId: 'getAllCategories'
  });

  if (!wpData?.results?.length) return [];

  const categories = await Promise.all(
    wpData?.results.map(async (result: any): Promise<{ name: string, slug: string }> => {

      return {
        name: result.name,
        slug: result.slug
      };
    })
  );

  return categories;
}

export function getBlogCommenttId(): string {
  const newDocRef = doc(blogCommentsRef);
  const id = newDocRef.id;
  return id;
}

export async function postComment(commentDoc: BlogCommentDocument) {
  const comment_id = getBlogCommenttId();
  const parsedData = blogCommentDocumentSchema.parse({
    ...commentDoc,
    comment_id,
  });
  const docRef = doc(blogCommentsRef, parsedData.comment_id);
  return setDoc(docRef, parsedData);
}

export async function deleteComment(args: DeleteCommentArgs) {
  const docRef = doc(blogCommentsRef, args.commentId);
  deleteDoc(docRef);
}

export async function getBlogComments(
  args: GetBlogCommentsArgs
): Promise<ApiResponse<BlogCommentDocument[]>> {
  const q = query(
    blogCommentsRef,
    where('blog_id', '==', args.blogId),
    where('parent_id', '==', args.parentId),
    orderBy('timestamp', 'desc')
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data());
  return {
    results,
  };
}
